import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { JobDescriptionTemplate, JobDescriptionTemplateBody } from 'src/app/models/job_description_template.model';
import { ApiService, TinymceService } from 'src/app/services';

export interface JobDescriptionTemplateFormDialogData {
  mode: 'create' | 'edit';
  job_description_template?: JobDescriptionTemplate;
}

@Component({
  selector: 'app-job-description-template-form-dialog',
  templateUrl: './job-description-template-form-dialog.component.html',
  styleUrl: './job-description-template-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    EditorComponent,
  ],
})
export class JobDescriptionTemplateFormDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<JobDescriptionTemplateFormDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly tinymce_service = inject(TinymceService);

  readonly data = inject<JobDescriptionTemplateFormDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly is_edit_mode = this.data.mode === 'edit';

  tiny_mce_api_key = '';

  readonly tiny_mce_init = {
    statusbar: false,
    menubar: false,
    height: '45vh',
    ui_mode: 'split' as const,
    plugins: 'lists link table wordcount',
    toolbar: 'undo redo | blocks | bold italic underline | bullist numlist | link table | removeformat',
  };

  readonly form = this.form_builder.group({
    name: [this.data.job_description_template?.name ?? '', [Validators.required, Validators.maxLength(255)]],
    html_body: [this.data.job_description_template?.html_body ?? '', Validators.required],
  });

  ngOnInit(): void {
    this.tiny_mce_api_key = this.tinymce_service.getKey();
  }

  save(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    this.loading.set(true);

    const raw = this.form.getRawValue();
    const body: JobDescriptionTemplateBody = {
      name: raw.name ?? '',
      html_body: raw.html_body ?? '',
    };

    const request$ =
      this.is_edit_mode && this.data.job_description_template
        ? this.api_service.updateJobDescriptionTemplate(this.data.job_description_template.id, body)
        : this.api_service.createJobDescriptionTemplate(body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: (): void => {
        this.dialog_ref.close(true);
      },
      error: (err: { error?: { error?: string } }): void => {
        this.loading.set(false);
        const message = err?.error?.error ?? 'Error al guardar la plantilla.';
        this.snack_bar.open(message, undefined, { duration: 5000 });
      },
    });
  }

  cancel(): void {
    this.dialog_ref.close(false);
  }
}
